import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useConversations } from "../context/ConversationsContext";
import { useDashboards } from "../context/DashboardsContext";
import * as api from "../api/client";
import { timeAgo } from "../utils/time";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    conversations,
    refresh,
    activeId,
    setActiveId,
    selected,
    toggleSelected,
    clearSelected,
    removeConversation,
  } = useConversations();
  const { dashboards, removeDashboard } = useDashboards();

  const [query, setQuery] = useState("");
  // Checkboxes only appear in select mode. Showing them permanently made every row look like a form
  // control and buried the thing people actually do here, which is open a chat.
  const [selectMode, setSelectMode] = useState(false);

  const visible = query.trim()
    ? conversations.filter((c) => (c.title || "").toLowerCase().includes(query.trim().toLowerCase()))
    : conversations;

  const openChat = (id) => {
    if (selectMode) return toggleSelected(id);
    setActiveId(id);
    navigate("/");
  };

  const newChat = () => {
    setActiveId(crypto.randomUUID());
    navigate("/");
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    clearSelected();
  };

  const onDeleteChat = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.deleteConversation(id);
    } catch {
      // best-effort; the row is removed locally either way
    }
    removeConversation(id);
    if (id === activeId) newChat();
  };

  const onDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selected.size} chat(s)? This cannot be undone.`)) return;
    for (const id of [...selected]) {
      try {
        await api.deleteConversation(id);
      } catch {
        // keep going; one failure shouldn't strand the rest
      }
      removeConversation(id);
    }
    exitSelectMode();
  };

  const onDeleteDashboard = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this dashboard? This cannot be undone.")) return;
    try {
      await api.deleteDashboard(id);
      removeDashboard(id);
      if (location.pathname === `/dashboards/${id}`) navigate("/dashboards");
    } catch (err) {
      window.alert("Could not delete dashboard: " + err.message);
    }
  };

  return (
    <aside className="sidebar">
      <div className="side-head">
        <span className="side-title">{selectMode ? `SELECTED (${selected.size})` : "CONVERSATIONS"}</span>
        <div className="side-head-actions">
          <button
            className={"icon-btn" + (selectMode ? " on" : "")}
            title={selectMode ? "Exit selection" : "Select chats for a dashboard"}
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
          >
            ☑
          </button>
          <button className="icon-btn" title="Refresh" onClick={refresh}>
            ⟳
          </button>
        </div>
      </div>

      <div className="side-search">
        <span className="side-search-icon">⌕</span>
        <input
          type="text"
          placeholder="Search conversations..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Selection actions sit ABOVE New Chat rather than replacing it, so starting a fresh chat is
          still one click away while you're picking conversations for a dashboard. */}
      {selectMode && (
        <div className="select-actions">
          <button
            className="btn-build"
            disabled={selected.size === 0}
            onClick={() => navigate("/dashboards/new")}
          >
            ⊞ Build
          </button>
          <button
            className="btn-trash"
            disabled={selected.size === 0}
            title="Delete selected"
            onClick={onDeleteSelected}
          >
            🗑
          </button>
          <button className="btn-cancel" onClick={exitSelectMode}>
            Cancel
          </button>
        </div>
      )}

      <button className="newchat" onClick={newChat}>
        <span className="plus">+</span> New Chat
      </button>

      <div className="conv-list">
        {conversations.length === 0 && <div className="conv-empty">No chats yet.</div>}
        {conversations.length > 0 && visible.length === 0 && (
          <div className="conv-empty">No chats match “{query.trim()}”.</div>
        )}
        {visible.map((c) => {
          const isActive = !selectMode && c.id === activeId && location.pathname === "/";
          return (
            <div
              key={c.id}
              className={"conv" + (isActive ? " active" : "") + (selected.has(c.id) ? " picked" : "")}
              title={c.title || ""}
              onClick={() => openChat(c.id)}
            >
              {selectMode ? (
                <input
                  type="checkbox"
                  className="conv-check"
                  checked={selected.has(c.id)}
                  onChange={() => toggleSelected(c.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="conv-icon">▤</span>
              )}
              <div className="conv-text">
                <div className="conv-row1">
                  <span className="conv-title">{c.title || "New chat"}</span>
                  <span className="conv-time">{timeAgo(c.updated_at)}</span>
                </div>
                <div className="conv-sub">{c.title || ""}</div>
              </div>
              {!selectMode && (
                <button className="conv-del" title="Delete chat" onClick={(e) => onDeleteChat(e, c.id)}>
                  🗑
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="side-sub-head" onClick={() => navigate("/dashboards")}>
        DASHBOARDS
      </div>
      <div className="conv-list dash-list">
        {dashboards.length === 0 && <div className="conv-empty">No dashboards yet.</div>}
        {dashboards.map((d) => (
          <div
            key={d.id}
            className={"conv" + (location.pathname === `/dashboards/${d.id}` ? " active" : "")}
            title={d.title}
            onClick={() => navigate(`/dashboards/${d.id}`)}
          >
            <span className="conv-icon dash-icon">▩</span>
            <div className="conv-text">
              <div className="conv-row1">
                <span className="conv-title">{d.title}</span>
                {/* G = global: shared with roles. The owner sees which ones; a viewer sees who shared it. */}
                {(d.sharedRoles || []).length > 0 && (
                  <span
                    className="dash-shared-badge"
                    title={
                      d.isOwner === false
                        ? `Shared with you by ${d.owner}`
                        : `Shared with: ${d.sharedRoles.join(", ")}`
                    }
                  >
                    G
                  </span>
                )}
                <span className="conv-time">{d.widgetCount}</span>
              </div>
            </div>
            {d.isOwner !== false && (
              <button className="conv-del" title="Delete dashboard" onClick={(e) => onDeleteDashboard(e, d.id)}>
                🗑
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="side-foot">
        <span>
          {conversations.length} CONVERSATION{conversations.length === 1 ? "" : "S"}
        </span>
        {conversations.length > 0 && (
          <button
            className="clear-all"
            onClick={() => {
              setSelectMode(true);
            }}
          >
            🗑 Clear All
          </button>
        )}
      </div>
    </aside>
  );
}
